import { type ReactNode, useState } from 'react';
import { Button, Text } from '@mantine/core';
import styled from 'styled-components';
import { DocumentViewerHeader } from '@dealstack/frontend/shared/components/DocumentViewerV2/common/DocumentViewerHeader.tsx';
import { PlacementCanvas } from './PlacementCanvas/PlacementCanvas.tsx';
import type {
	CanvasPlacement,
	FixedPlacement,
	PdfBox,
	SlotMeta,
} from './PlacementCanvas/PlacementCanvas.types.ts';
import { SlotList } from './SlotList/SlotList.tsx';
import type { SlotListItem } from './SlotList/SlotList.types.ts';
import { MIN_SCALE, MAX_SCALE, SCALE_STEP } from './SignaturePlacementEditor.types.ts';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

// Presentational shell shared by the two placement editors. Owns zoom +
// visible-page state and the header/split/canvas/side-panel skeleton; editors
// supply the document, controller wiring, and a footer render-slot.
interface Props {
	file: Blob | string;
	showMetadata: boolean;
	onShowMetadataChange: (showMetadata: boolean) => void;
	placements: Map<string, CanvasPlacement>;
	/** Other parties' boxes, drawn read-only and kept clear of. */
	fixedPlacements?: FixedPlacement[];
	slotByKey: Map<string, SlotMeta>;
	selectedSlotKey: string | null;
	onSelect: (key: string | null) => void;
	onPlacementChange: (key: string, pageNum: number, pdfBox: PdfBox) => void;
	onRemove: (key: string) => void;
	/** Drop the slot at its default position on the currently-visible page. */
	onPlaceAtDefault: (key: string, pageNum: number) => void;
	slotListItems: SlotListItem[];
	placedCount: number;
	totalCount: number;
	isComplete: boolean;
	/** Back-arrow handler in the header. */
	onBack?: () => void;
	/** Footer buttons; rendered in the side-panel footer when provided. */
	footer?: ReactNode;
	/** Pinned at the top of the side panel — the editor-wide Data points / Signatures bar. */
	paletteTabs?: ReactNode;
	/**
	 * Canvas zoom, when a host shares one across surfaces. Uncontrolled otherwise.
	 *
	 * The tabbed editor owns it so the page does not resize under the author on a tab switch;
	 * a standalone caller passes neither and keeps this layout's own default.
	 */
	scale?: number;
	onScaleChange?: (scale: number) => void;
	/** Why this document cannot use manual placement. Shown above the slot list when set. */
	blockedReason?: string;
	/** When provided, renders an "Add date field" button targeting the visible page. */
	onAddDateField?: (pageNum: number) => void;
	dateFieldCount?: number;
	/** False in the dates-only editor: the header then reports the date count alone. */
	showSignatureSummary?: boolean;
}

export const PlacementEditorLayout = ({
	file,
	showMetadata,
	onShowMetadataChange,
	placements,
	fixedPlacements,
	slotByKey,
	selectedSlotKey,
	onSelect,
	onPlacementChange,
	onRemove,
	onPlaceAtDefault,
	slotListItems,
	placedCount,
	totalCount,
	isComplete,
	onBack,
	footer,
	paletteTabs,
	scale: controlledScale,
	onScaleChange,
	blockedReason,
	onAddDateField,
	dateFieldCount,
	showSignatureSummary = true,
}: Props) => {
	// Controlled when the tabbed editor shares one zoom across its surfaces; its own otherwise,
	// so a standalone caller is unaffected.
	const [ownScale, setOwnScale] = useState(1);
	const scale = controlledScale ?? ownScale;
	const setScale = onScaleChange ?? setOwnScale;
	const [currentPageNum, setCurrentPageNum] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	const onUpscale = () => setScale(Math.min(MAX_SCALE, scale + SCALE_STEP));
	const onDownscale = () => setScale(Math.max(MIN_SCALE, scale - SCALE_STEP));

	const handlePlace = (key: string) => onPlaceAtDefault(key, currentPageNum);

	const dateCount = dateFieldCount ?? 0;
	const dateSummary = `${dateCount} date field${dateCount === 1 ? '' : 's'}`;
	const signatureSummary = isComplete
		? `All ${totalCount} signature${totalCount === 1 ? '' : 's'} placed`
		: `${placedCount} of ${totalCount} placed`;
	const placementSummary = showSignatureSummary
		? `${signatureSummary}${dateCount > 0 ? `, ${dateSummary}` : ''}`
		: dateSummary;

	return (
		<Wrapper>
			<DocumentViewerHeader
				title={placementSummary}
				currentPage={totalPages > 0 ? currentPageNum : undefined}
				totalPages={totalPages > 0 ? totalPages : undefined}
				onUpscale={onUpscale}
				onDownscale={onDownscale}
				onBackArrowClick={onBack}
				showBackButton
			/>
			<Split>
				<CanvasPane>
					<PlacementCanvas
						file={file}
						scale={scale}
						placements={placements}
						{...(fixedPlacements === undefined ? {} : { fixedPlacements })}
						selectedSlotKey={selectedSlotKey}
						slotByKey={slotByKey}
						showMetadata={showMetadata}
						onSelect={onSelect}
						onPlacementChange={onPlacementChange}
						onVisiblePageChange={setCurrentPageNum}
						onTotalPagesChange={setTotalPages}
						onRemove={onRemove}
					/>
				</CanvasPane>
				<SidePanel>
					{paletteTabs}
					<SidePanelHeader>
						{/* Shown where the author is trying to place something, rather than beside a
						    toggle on the form — there is no longer a toggle. */}
						{blockedReason !== undefined && (
							<Text size="xs" c="red" data-testid="placement-blocked-reason">
								{blockedReason}
							</Text>
						)}
						<AdvancedSwitch
							rightText="Show details"
							selectedValue={showMetadata}
							onChange={(event) => onShowMetadataChange(!!event)}
							selectedColor="dark.8"
							unselectedColor="dark.8"
						/>
					</SidePanelHeader>
					{onAddDateField ? (
						<Button
							variant="light"
							m="0.75rem 0.75rem 0"
							onClick={() => onAddDateField(currentPageNum)}
							data-testid={TEST_ELEMENT_IDS.PLACEMENT_EDITOR_ADD_DATE_FIELD_BUTTON}
						>
							Add date field
						</Button>
					) : null}
					<SlotListScroller>
						<SlotList
							slots={slotListItems}
							selectedSlotKey={selectedSlotKey}
							onSelect={onSelect}
							onLocate={onSelect}
							onPlace={handlePlace}
						/>
					</SlotListScroller>
					{footer && <PanelFooter>{footer}</PanelFooter>}
				</SidePanel>
			</Split>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	min-height: 0;
`;

const Split = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 320px;
	// minmax(0, 1fr) not bare 1fr (= minmax(auto, 1fr)): caps the row so the
	// PDF canvas can't push past the parent and the SidePanel footer stays visible.
	grid-template-rows: minmax(0, 1fr);
	gap: 1rem;
	flex: 1;
	min-height: 0;
`;

const CanvasPane = styled.div`
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
`;

const SidePanel = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 4px;
	overflow: hidden;
`;

const SidePanelHeader = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	padding: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	flex-shrink: 0;
`;

const SlotListScroller = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 0.75rem;
`;

const PanelFooter = styled.div`
	border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	padding: 0.75rem;
	display: flex;
	gap: 0.5rem;
	flex-shrink: 0;

	& > button {
		flex: 1;
		min-width: 0;
	}
`;
